/**
 * MineRadio playback controller.
 *
 * Queue/list semantics are delegated to `PlaybackQueue`, adapted from
 * YesPlayMusic's MIT-licensed Player.js. Source resolution and the single
 * browser Audio element remain application-owned boundaries.
 */

import { PlaybackQueue } from './playback-queue.ts';

export type PlaybackMode = 'sequence' | 'repeat-all' | 'repeat-one' | 'shuffle';

export function clampSeekTime(target: number, duration: number, ranges: readonly [number, number][] = []): number | null {
  if (!Number.isFinite(target) || !Number.isFinite(duration) || duration <= 0) return null;
  const bounded = Math.max(0, Math.min(duration, target));
  const valid = ranges.filter(([start, end]) => Number.isFinite(start) && Number.isFinite(end) && start < end);
  if (!valid.length || valid.some(([start, end]) => bounded >= start && bounded <= end)) return bounded;
  return valid.flat().reduce((closest, value) => Math.abs(value - bounded) < Math.abs(closest - bounded) ? value : closest);
}

export type ResolveResult = string | null | undefined;
export type ResolvePurpose = 'play' | 'prefetch';

export type PlayResult =
  | { status: 'played'; requestId: number }
  | { status: 'stale'; requestId: number }
  | { status: 'unavailable'; requestId: number }
  | { status: 'error'; requestId: number; error: unknown };

export interface PlaybackControllerOptions {
  /** Resolve an ID to a playable URL. The signal is aborted when a newer play wins. */
  resolveSource: (id: string, signal: AbortSignal, purpose?: ResolvePurpose) => Promise<ResolveResult>;
  /** Resolve the next candidate without touching the active media element. */
  prefetchSource?: (id: string, signal: AbortSignal) => Promise<ResolveResult>;
  /** Include source/quality preferences in the short-lived in-memory cache key. */
  prefetchKey?: (id: string) => string;
  /** Apply the resolved URL to the single application audio element. */
  setSource: (url: string) => void;
  /** Start playback after setSource. */
  play: () => Promise<void>;
  /** Stop the previous source before replacing it. */
  pause: () => void;
  onCurrentChange?: (id: string | null) => void;
  /** Publish the selected identity before a slow source resolver completes. */
  onSelectionChange?: (id: string, requestId: number) => void;
  /** Notify the host when a prefetched URL is promoted to playback. */
  onPrefetchUsed?: (id: string, url: string, signal: AbortSignal) => boolean | void;
  onPrefetchDiscarded?: () => void;
  onSourceReady?: (id: string, signal: AbortSignal) => void;
  onUnavailable?: (id: string) => void;
  onError?: (id: string, error: unknown, stage: 'resolve' | 'play') => void;
}

export interface PlaybackSnapshot {
  currentId: string | null;
  mode: PlaybackMode;
  list: string[];
  queue: string[];
  playNext: string[];
  requestId: number;
}

const validModes: readonly PlaybackMode[] = ['sequence', 'repeat-all', 'repeat-one', 'shuffle'];

const uniqueIds = (ids: readonly string[]) => {
  const seen = new Set<string>();
  return ids.filter((id) => {
    if (!id || seen.has(id)) return false;
    seen.add(id);
    return true;
  });
};

/**
 * Generic single-audio playback controller.
 *
 * Queue semantics intentionally distinguish the persistent queue (`queue`)
 * from one-shot entries (`playNext`). A one-shot entry is consumed before the
 * normal list, matching the useful part of YesPlayMusic's play-next behavior.
 */
export class PlayerController {
  private readonly options: PlaybackControllerOptions;
  private listIds: string[] = [];
  private queueIds: string[] = [];
  private playNextIds: string[] = [];
  private readonly queueModel = new PlaybackQueue();
  private currentIdValue: string | null = null;
  private listCursorId: string | null = null;
  private currentIsInsertion = false;
  private nextSelection: { id: string; insertion: boolean } | null = null;
  private modeValue: PlaybackMode = 'sequence';
  private shuffleRemaining: string[] = [];
  private requestCounter = 0;
  private resolveAbort: AbortController | null = null;
  private prefetchAbort: AbortController | null = null;
  private prefetchCounter = 0;
  private prefetchCache: { id: string; key: string; url: string; expiresAt: number } | null = null;
  private prefetchTarget: { id: string; key: string } | null = null;

  public constructor(options: PlaybackControllerOptions) {
    this.options = options;
  }

  public get currentId(): string | null {
    return this.currentIdValue;
  }

  public get mode(): PlaybackMode {
    return this.modeValue;
  }

  public setList(ids: readonly string[]): void {
    const next = uniqueIds(ids);
    const current = this.currentIdValue;
    const currentWasInList = this.listIds.includes(current || '');
    this.listIds = next;
    if (this.listCursorId && !next.includes(this.listCursorId)) this.listCursorId = null;
    if (current && next.includes(current) && !this.currentIsInsertion) this.listCursorId = current;
    this.syncQueueModel();
    if (current && currentWasInList && !next.includes(current) && !this.currentIsInsertion) {
      this.currentIdValue = null;
      this.options.onCurrentChange?.(null);
    }
  }

  public setQueue(ids: readonly string[]): void {
    this.queueIds = uniqueIds(ids);
    this.syncQueueModel();
  }

  public getQueue(): string[] {
    return [...this.queueIds];
  }

  public enqueue(id: string): boolean {
    if (!id || this.queueIds.includes(id)) return false;
    this.queueIds.push(id);
    this.syncQueueModel();
    return true;
  }

  public removeFromQueue(id: string): boolean {
    const before = this.queueIds.length;
    this.queueIds = this.queueIds.filter((item) => item !== id);
    if (before !== this.queueIds.length) this.syncQueueModel();
    return before !== this.queueIds.length;
  }

  /** Add a one-shot ID to the front of the next-track stack. */
  public enqueueNext(id: string): boolean {
    if (!id || this.playNextIds.includes(id) || id === this.currentIdValue) return false;
    this.playNextIds.push(id);
    this.syncQueueModel();
    return true;
  }

  public clearPlayNext(): void {
    this.playNextIds = [];
    this.syncQueueModel();
  }

  public setPlayNext(ids: readonly string[]): void {
    this.playNextIds = [...ids];
    this.syncQueueModel();
  }

  public getPlayNext(): string[] {
    return [...this.playNextIds];
  }

  public setMode(mode: PlaybackMode): void {
    if (!validModes.includes(mode)) return;
    if (mode !== this.modeValue) {
      this.modeValue = mode;
      this.syncQueueModel();
    }
  }

  public setCurrent(id: string | null): void {
    const changed = id !== this.currentIdValue;
    const selected = this.nextSelection?.id === id ? this.nextSelection : null;
    const insertion = id !== null && (selected ? selected.insertion :
      this.playNextIds.includes(id) || (!changed && this.currentIsInsertion));
    this.nextSelection = null;
    this.currentIdValue = id;
    this.currentIsInsertion = insertion;
    if (!id) this.listCursorId = null;
    else if (!insertion && this.listIds.includes(id)) this.listCursorId = id;
    this.playNextIds = this.playNextIds.filter(item => item !== id);
    this.syncQueueModel();
    if (changed) this.options.onCurrentChange?.(id);
  }

  /**
   * Resolve and play a track. Older asynchronous resolutions cannot replace
   * the source after a newer request has started.
   */
  public async play(id: string): Promise<PlayResult> {
    const requestId = ++this.requestCounter;
    this.resolveAbort?.abort();
    const abort = new AbortController();
    this.resolveAbort = abort;
    this.options.pause();
    this.options.onSelectionChange?.(id, requestId);

    let source: ResolveResult;
    try {
      const prefetched = this.takePrefetched(id, abort.signal);
      if (!prefetched) this.clearPrefetch();
      source = prefetched || await this.options.resolveSource(id, abort.signal, 'play');
    } catch (error) {
      if (!this.isCurrentRequest(requestId) || abort.signal.aborted) {
        return { status: 'stale', requestId };
      }
      this.options.onError?.(id, error, 'resolve');
      return { status: 'error', requestId, error };
    }

    if (!this.isCurrentRequest(requestId) || abort.signal.aborted) {
      return { status: 'stale', requestId };
    }
    if (!source) {
      this.options.onUnavailable?.(id);
      return { status: 'unavailable', requestId };
    }

    try {
      this.options.setSource(source);
      this.setCurrent(id);
      this.options.onSourceReady?.(id, abort.signal);
      await this.options.play();
    } catch (error) {
      if (!this.isCurrentRequest(requestId) || abort.signal.aborted) {
        return { status: 'stale', requestId };
      }
      this.options.onError?.(id, error, 'play');
      return { status: 'error', requestId, error };
    }
    if (!this.isCurrentRequest(requestId) || abort.signal.aborted) {
      return { status: 'stale', requestId };
    }
    return { status: 'played', requestId };
  }

  /** Start a newer request without resolving a source. */
  public cancelPendingPlay(): number {
    this.resolveAbort?.abort();
    this.resolveAbort = null;
    return ++this.requestCounter;
  }

  /**
   * Resolve only the immediately upcoming entry. The result lives in memory
   * for a short window and is invalidated by a newer queue/current snapshot;
   * no second Audio element is created and no signed URL is persisted.
   */
  public prefetchNext(): void {
    const source = this.options.prefetchSource || ((id: string, signal: AbortSignal) => this.options.resolveSource(id, signal, 'prefetch'));
    const id = this.peekNextId();
    const key = id ? this.options.prefetchKey?.(id) || id : '';
    if (id && this.prefetchTarget?.id === id && this.prefetchTarget.key === key &&
      (!this.prefetchCache || this.prefetchCache.expiresAt > Date.now())) return;
    this.clearPrefetch();
    if (!source || !id) return;
    const requestId = ++this.prefetchCounter;
    const abort = new AbortController();
    this.prefetchAbort = abort;
    this.prefetchTarget = { id, key };
    void source(id, abort.signal).then((url) => {
      if (abort.signal.aborted || requestId !== this.prefetchCounter || !url) return;
      this.prefetchCache = { id, key, url, expiresAt: Date.now() + 30_000 };
    }).catch(() => {
      // Prefetch is opportunistic. Playback still resolves the source normally.
    });
  }

  public clearPrefetch(): void {
    this.prefetchAbort?.abort();
    this.prefetchAbort = null;
    this.prefetchCache = null;
    this.prefetchTarget = null;
    this.prefetchCounter += 1;
    this.options.onPrefetchDiscarded?.();
  }

  public isCurrentRequest(requestId: number): boolean {
    return requestId === this.requestCounter;
  }

  private takePrefetched(id: string, signal: AbortSignal): string | null {
    const cached = this.prefetchCache;
    if (!cached || cached.id !== id || cached.expiresAt <= Date.now()) return null;
    if ((this.options.prefetchKey?.(id) || id) !== cached.key) return null;
    if (this.options.onPrefetchUsed?.(id, cached.url, signal) === false) return null;
    this.prefetchCache = null;
    this.prefetchTarget = null;
    this.prefetchAbort = null;
    return cached.url;
  }

  private peekNextId(): string | null {
    if (!this.currentIdValue) return null;
    if (this.playNextIds.length) return this.playNextIds[0] || null;
    if (this.modeValue === 'repeat-one') return null;
    const next = this.queueModel.peek({ direction: 1, mode: this.modeValue === 'shuffle' ? 'sequence' : this.modeValue, shuffle: this.modeValue === 'shuffle' });
    if (this.currentIsInsertion && next === this.currentIdValue) {
      // `next()` skips an inserted recording that is also the normal next
      // list entry. Match that skip without advancing either queue cursor.
      if (this.modeValue === 'shuffle') return null;
      const index = this.listIds.indexOf(next);
      return this.listIds[index + 1] || (this.modeValue === 'repeat-all' ? this.listIds[0] || null : null);
    }
    return next;
  }

  /**
   * Select the next or previous ID without mutating the audio element. Callers
   * can pass the returned ID back to `play` after updating their persisted state.
   */
  public next(direction: 1 | -1 = 1, cause: 'manual' | 'ended' = 'manual'): string | null {
    const insertion = direction > 0 && this.playNextIds.length > 0;
    if (direction > 0 && cause === 'ended' && this.modeValue === 'repeat-one' && !insertion && this.currentIdValue) {
      this.nextSelection = { id: this.currentIdValue, insertion: this.currentIsInsertion };
      return this.currentIdValue;
    }
    const options = {
      direction,
      cause,
      mode: this.modeValue === 'shuffle' ? 'sequence' : this.modeValue,
      shuffle: this.modeValue === 'shuffle',
    } as const;
    let next = this.queueModel.next(options);
    if (!insertion && this.currentIsInsertion && next === this.currentIdValue) {
      // The inserted song may also be the normal next entry. Consume that
      // position without playing the same recording a second time in a row.
      this.listCursorId = next;
      this.queueModel.setCurrent(next);
      next = this.queueModel.next(options);
    }
    this.playNextIds = this.queueModel.getPlayNext();
    this.nextSelection = next ? { id: next, insertion } : null;
    return next;
  }

  public snapshot(): PlaybackSnapshot {
    return {
      currentId: this.currentIdValue,
      mode: this.modeValue,
      list: [...this.listIds],
      queue: [...this.queueIds],
      playNext: [...this.playNextIds],
      requestId: this.requestCounter,
    };
  }

  private syncQueueModel(): void {
    this.clearPrefetch();
    // Keep the active playback context (`list`) separate from the persisted
    // queue snapshot. A track started outside the queue must continue within
    // its active list instead of jumping to a stale queue entry.
    // Insertions have a real current ID but must not move the normal-list
    // cursor. Resume after that cursor when the one-shot entries are consumed.
    this.queueModel.setList(this.listIds, this.currentIsInsertion ? this.listCursorId : this.currentIdValue);
    this.queueModel.setPlayNext(this.playNextIds.filter(id => id !== this.currentIdValue));
  }
}

export type BlobInput = BlobPart | ArrayBuffer | ArrayBufferView;

/**
 * Bounded object URL registry. URLs are touched in insertion order and the
 * oldest untouched entry is revoked once the bound is reached. `revokeAll`
 * is intended for app shutdown or a full library reset.
 */
export class BlobUrlRegistry {
  private readonly entries = new Map<string, string>();
  private readonly protectedKeys = new Set<string>();
  private readonly maxEntries: number;
  private readonly createUrl: (blob: Blob) => string;
  private readonly revokeUrl: (url: string) => void;

  public constructor(
    maxEntries = 50,
    createUrl: (blob: Blob) => string = (blob) => URL.createObjectURL(blob),
    revokeUrl: (url: string) => void = (url) => URL.revokeObjectURL(url),
  ) {
    if (!Number.isInteger(maxEntries) || maxEntries < 1) throw new RangeError('maxEntries must be >= 1');
    this.maxEntries = maxEntries;
    this.createUrl = createUrl;
    this.revokeUrl = revokeUrl;
  }

  public create(data: BlobInput, mimeType: string, key: string): string {
    if (!key) throw new Error('Blob URL key is required');
    const existing = this.entries.get(key);
    if (existing) {
      this.entries.delete(key);
      this.entries.set(key, existing);
      return existing;
    }
    while (this.entries.size >= this.maxEntries) {
      const oldest = [...this.entries.keys()].find((entry) => !this.protectedKeys.has(entry));
      if (!oldest) throw new Error('All Blob URLs are in use');
      this.revoke(oldest);
    }
    const blob = data instanceof Blob ? data : new Blob([data as BlobPart], { type: mimeType });
    const url = this.createUrl(blob);
    this.entries.set(key, url);
    return url;
  }

  public get(key: string): string | null {
    const url = this.entries.get(key);
    if (!url) return null;
    this.entries.delete(key);
    this.entries.set(key, url);
    return url;
  }

  public has(key: string): boolean {
    return this.entries.has(key);
  }

  public protect(key: string): void {
    this.protectedKeys.add(key);
  }

  public unprotect(key: string): void {
    this.protectedKeys.delete(key);
  }

  public revoke(key: string): void {
    const url = this.entries.get(key);
    if (!url) return;
    this.revokeUrl(url);
    this.entries.delete(key);
    this.protectedKeys.delete(key);
  }

  public revokeAll(): void {
    for (const url of this.entries.values()) this.revokeUrl(url);
    this.entries.clear();
    this.protectedKeys.clear();
  }

  public get size(): number {
    return this.entries.size;
  }
}

